import { apiRequest } from './client'
import { demoCatalogFilterOptions, demoCatalogSearchPage } from '../dev/buyerDemoData'
import { withDemoUi } from '../dev/demoMode'
import { readInt, readString } from './json'

export type RestaurantCatalogOfferDto = {
  itemId: number
  supplierOrganizationId: number | null
  supplierOrganizationName: string
  privateSupplierId: number | null
  legalName: string | null
  inn: string | null
  isRegisteredSupplier: boolean
  itemName: string
  brand: string | null
  country: string | null
  unit: string | null
  price: string | null
  minShipmentQty: string | null
  packageSize: string | null
  productTypeId: number | null
  productTypeCode: string | null
  productTypeName: string | null
  productTypeActive: boolean | null
  supplierResponseScore: string | null
  rankingPrice: string | null
  matchedTags: string[]
}

export type RestaurantCatalogInterpretationDto = {
  ordering: string
  suppliersCount: number
  matchedItemsCount: number
  tagMatchedItemsCount: number
  cheapestPrice: string | null
  highestPrice: string | null
  note: string | null
}

export type RestaurantCatalogSearchPageDto = {
  items: RestaurantCatalogOfferDto[]
  page: number
  size: number
  totalElements: number
  totalPages: number
  interpretation: RestaurantCatalogInterpretationDto | null
}

export type RestaurantCatalogFilterOptionsDto = {
  brands: string[]
  countries: string[]
}

export type RestaurantCatalogSearchRequest = {
  organizationId: number
  productTypeIds?: number[]
  country?: string
  brand?: string
  minPrice?: string
  maxPrice?: string
  customTags?: string[]
  myPricesOnly?: boolean
  supplierOrganizationId?: number | null
  privateSupplierId?: number | null
  matchStatus?: string
  unassignedProductType?: boolean
  page?: number
  size?: number
}

type Raw = Record<string, unknown>

const normalizeOffer = (json: Raw): RestaurantCatalogOfferDto => {
  const tags =
    (json.matchedTags as string[] | undefined) ??
    (json.matched_tags as string[] | undefined) ??
    []
  return {
    itemId: readInt(json, 'itemId', 'item_id'),
    supplierOrganizationId:
      json.supplierOrganizationId != null || json.supplier_organization_id != null
        ? readInt(json, 'supplierOrganizationId', 'supplier_organization_id')
        : null,
    supplierOrganizationName:
      readString(json, 'supplierOrganizationName', 'supplier_organization_name') ?? '',
    privateSupplierId:
      json.privateSupplierId != null || json.private_supplier_id != null
        ? readInt(json, 'privateSupplierId', 'private_supplier_id')
        : null,
    legalName: readString(json, 'legalName', 'legal_name'),
    inn: readString(json, 'inn', 'inn'),
    isRegisteredSupplier:
      json.isRegisteredSupplier != null
        ? Boolean(json.isRegisteredSupplier)
        : json.is_registered_supplier != null
          ? Boolean(json.is_registered_supplier)
          : true,
    itemName: readString(json, 'itemName', 'item_name') ?? '',
    brand: (json.brand as string | null) ?? null,
    country: (json.country as string | null) ?? null,
    unit: (json.unit as string | null) ?? null,
    price: json.price != null ? String(json.price) : null,
    minShipmentQty:
      json.minShipmentQty != null
        ? String(json.minShipmentQty)
        : json.min_shipment_qty != null
          ? String(json.min_shipment_qty)
          : null,
    packageSize: readString(json, 'packageSize', 'package_size'),
    productTypeId:
      json.productTypeId != null || json.product_type_id != null
        ? readInt(json, 'productTypeId', 'product_type_id')
        : null,
    productTypeCode: readString(json, 'productTypeCode', 'product_type_code'),
    productTypeName: readString(json, 'productTypeName', 'product_type_name'),
    productTypeActive:
      json.productTypeActive != null
        ? Boolean(json.productTypeActive)
        : json.product_type_active != null
          ? Boolean(json.product_type_active)
          : null,
    supplierResponseScore:
      json.supplierResponseScore != null
        ? String(json.supplierResponseScore)
        : json.supplier_response_score != null
          ? String(json.supplier_response_score)
          : null,
    rankingPrice:
      json.rankingPrice != null
        ? String(json.rankingPrice)
        : json.ranking_price != null
          ? String(json.ranking_price)
          : null,
    matchedTags: tags.map(String),
  }
}

const normalizeInterpretation = (json: Raw): RestaurantCatalogInterpretationDto => ({
  ordering: (json.ordering as string) ?? '',
  suppliersCount: readInt(json, 'suppliersCount', 'suppliers_count'),
  matchedItemsCount: readInt(json, 'matchedItemsCount', 'matched_items_count'),
  tagMatchedItemsCount: readInt(json, 'tagMatchedItemsCount', 'tag_matched_items_count'),
  cheapestPrice:
    json.cheapestPrice != null
      ? String(json.cheapestPrice)
      : json.cheapest_price != null
        ? String(json.cheapest_price)
        : null,
  highestPrice:
    json.highestPrice != null
      ? String(json.highestPrice)
      : json.highest_price != null
        ? String(json.highest_price)
        : null,
  note: (json.note as string | null) ?? null,
})

const normalizePage = (json: Raw): RestaurantCatalogSearchPageDto => {
  const rawItems = (json.items as Raw[] | undefined) ?? []
  return {
    items: rawItems.map(normalizeOffer),
    page: Number(json.page ?? 0),
    size: Number(json.size ?? 0),
    totalElements: Number(json.totalElements ?? json.total_elements ?? 0),
    totalPages: Number(json.totalPages ?? json.total_pages ?? 0),
    interpretation:
      json.interpretation != null
        ? normalizeInterpretation(json.interpretation as Raw)
        : null,
  }
}

export const searchRestaurantCatalog = async (
  request: RestaurantCatalogSearchRequest,
): Promise<RestaurantCatalogSearchPageDto> =>
  withDemoUi(demoCatalogSearchPage(), async () => {
  const body: Record<string, unknown> = {
    organization_id: request.organizationId,
    page: request.page ?? 0,
    size: request.size ?? 50,
  }
  if (request.productTypeIds?.length) {
    body.product_type_ids = request.productTypeIds
  }
  if (request.country?.trim()) {
    body.country = request.country.trim()
  }
  if (request.brand?.trim()) {
    body.brand = request.brand.trim()
  }
  if (request.minPrice?.trim()) {
    body.min_price = request.minPrice.trim()
  }
  if (request.maxPrice?.trim()) {
    body.max_price = request.maxPrice.trim()
  }
  if (request.customTags?.length) {
    body.custom_tags = request.customTags
  }
  if (request.myPricesOnly) {
    body.my_prices_only = true
  }
  if (request.supplierOrganizationId != null) {
    body.supplier_organization_id = request.supplierOrganizationId
  }
  if (request.privateSupplierId != null) {
    body.private_supplier_id = request.privateSupplierId
  }
  if (request.matchStatus?.trim()) {
    body.match_status = request.matchStatus.trim()
  }
  if (request.unassignedProductType) {
    body.unassigned_product_type = true
  }

  const page = await apiRequest<Raw>('/buyer/catalog/search', { method: 'POST', body })
  return normalizePage(page)
})

export const listRestaurantCatalogFilterOptions = async (
  organizationId: number,
  myPricesOnly?: boolean,
): Promise<RestaurantCatalogFilterOptionsDto> =>
  withDemoUi(demoCatalogFilterOptions(), async () => {
  const params = new URLSearchParams({ organization_id: String(organizationId) })
  if (myPricesOnly) {
    params.set('my_prices_only', 'true')
  }
  const response = await apiRequest<Raw>(`/buyer/catalog/filter-options?${params.toString()}`)
  const brands = (response.brands as string[] | undefined) ?? []
  const countries = (response.countries as string[] | undefined) ?? []
  return { brands, countries }
})
